/**
 * Session persistence helpers for CoPilotGuide.
 *
 * Kept in a separate file so CoPilotGuide.tsx contains only React components
 * and satisfies Vite Fast Refresh's requirement that component files must not
 * mix component exports with plain function exports.
 *
 * Keying scheme — localStorage entries are scoped to BOTH `flowId` AND
 * `journeyId`. Without journey-scoping, switching journeys via the
 * global selector would replay the OTHER journey's saved chat into
 * the current one. Observed 2026-04-26: user starts CheckUp on
 * Journey A, switches to Journey B (already completed), clicks Retake
 * — Journey B's brand-new server-side state is correctly returned by
 * the API, but the localStorage cache for `copilot-guide-state-checkup`
 * (flat, journey-agnostic) loaded Journey A's chat on top, hijacking
 * the engine.
 *
 * The journeyId is appended to the key. Pre-2026-04-26 entries (no
 * journey suffix) are silently orphaned and pruned by the 30-minute
 * TTL — no migration needed.
 */

import type { IMessage } from './types';
import type { CoPilotGuideEngine } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

const SESSION_KEY_PREFIX = 'copilot-guide-state-';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Compose the localStorage key for (flowId, journeyId).
 *
 * `journeyId` is REQUIRED for assessments scoped to a journey. Pass
 * `null` only for flows that intentionally have no journey scope (e.g.
 * `journey_builder` — the journey doesn't exist yet at runtime).
 */
function getSessionKey(flowId: string | undefined, journeyId: string | null | undefined): string {
  const flow = flowId || 'unknown';
  const journey = journeyId ?? 'no-journey';
  return `${SESSION_KEY_PREFIX}${flow}::${journey}`;
}

// ═══════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════

export function saveSessionState(
  flowId: string | undefined,
  journeyId: string | null | undefined,
  messages: IMessage[],
  engineState: ReturnType<CoPilotGuideEngine['getState']>
) {
  try {
    const key = getSessionKey(flowId, journeyId);
    const serializable = {
      messages: messages.map((m) => ({
        ...m,
        timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
      })),
      engineState: {
        currentTaskId: engineState.currentTaskId,
        currentChatId: engineState.currentChatId,
        completedTasks: Array.from(engineState.completedTasks),
        completedChats: Array.from(engineState.completedChats),
        variables: engineState.variables,
      },
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(serializable));

    console.log('[CP:LS] save', {
      key,
      msgs: messages.length,
      currentChatId: engineState.currentChatId,
      completedChats: Array.from(engineState.completedChats).length,
    });
  } catch (err) {
    console.warn('[CP:LS] save FAILED', { flowId, journeyId, err });
  }
}

export function loadSessionState(
  flowId: string | undefined,
  journeyId: string | null | undefined
): {
  messages: IMessage[];
  engineState: {
    currentTaskId: string | null;
    currentChatId: string | null;
    completedTasks: string[];
    completedChats: string[];
    variables: Record<string, unknown>;
  };
} | null {
  try {
    const key = getSessionKey(flowId, journeyId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      console.log('[CP:LS] load MISS', { key });
      return null;
    }

    const parsed = JSON.parse(raw);

    if (Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      localStorage.removeItem(key);

      console.log('[CP:LS] load MISS expired', { key, ageMs: Date.now() - parsed.savedAt });
      return null;
    }

    parsed.messages = parsed.messages.map((m: IMessage & { timestamp: string }) => ({
      ...m,
      timestamp: new Date(m.timestamp),
    }));

    console.log('[CP:LS] load HIT', {
      key,
      msgs: parsed.messages.length,
      currentChatId: parsed.engineState?.currentChatId,
      completedChats: parsed.engineState?.completedChats?.length,
      ageMs: Date.now() - parsed.savedAt,
    });
    return parsed;
  } catch (err) {
    console.warn('[CP:LS] load FAILED', { flowId, journeyId, err });
    return null;
  }
}

export function clearSessionState(
  flowId: string | undefined,
  journeyId: string | null | undefined
) {
  try {
    const key = getSessionKey(flowId, journeyId);
    const hadEntry = localStorage.getItem(key) !== null;
    localStorage.removeItem(key);

    console.log('[CP:LS] clear', { key, hadEntry });
  } catch (err) {
    console.warn('[CP:LS] clear FAILED', { flowId, journeyId, err });
  }
}

/**
 * Best-effort cleanup of stale entries written before the journey-key
 * scoping landed (2026-04-26). Strips legacy `copilot-guide-state-<flow>`
 * keys (no `::` separator). Safe to call on app boot — no-ops once
 * the legacy entries are gone, and the 30-min TTL would have pruned
 * them anyway.
 */
export function pruneLegacySessionKeys(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        key.startsWith(SESSION_KEY_PREFIX) &&
        !key.slice(SESSION_KEY_PREFIX.length).includes('::')
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
